import checkIconSource from "bootstrap-icons/icons/check-lg.svg?raw";
import type { HoverPointerModel } from "@/features/hoverIdle/input/hoverPointerModel";
import type { Disposable } from "@/shared/contracts/lifecycle";
import { MpSlotChargeController } from "./mpSlotChargeController";

export interface EnabledHoverToggleSyncState {
  readonly enabled: boolean;
  readonly disabled?: boolean;
  readonly ariaLabel?: string;
}

export interface EnabledHoverToggle extends Disposable {
  readonly element: HTMLButtonElement;
  clearPointerHover(): void;
  sync(state: EnabledHoverToggleSyncState): void;
}

interface CreateEnabledHoverToggleOptions {
  readonly pointerModel?: HoverPointerModel;
  readonly action: string;
  readonly actionSection?: string;
  readonly ariaLabel: string;
  readonly additionalClassName?: string;
  readonly iconSource?: string;
  readonly getChargeMs?: () => number;
  readonly onEnabledChange: (enabled: boolean) => void;
}

export function createEnabledHoverToggle(
  options: CreateEnabledHoverToggleOptions,
): EnabledHoverToggle {
  const button = document.createElement("button");
  button.type = "button";
  button.className = [
    "hover-idle-mp-slot",
    "hover-idle-mpm-enabled-toggle",
    options.additionalClassName,
  ]
    .filter((className): className is string => Boolean(className))
    .join(" ");
  button.dataset.action = options.action;
  if (options.actionSection !== undefined) {
    button.dataset.actionSection = options.actionSection;
  }
  button.dataset.virtualPointer = "allow";
  button.setAttribute("aria-label", options.ariaLabel);

  const fill = document.createElement("span");
  fill.className = "hover-idle-mp-slot-fill";
  fill.setAttribute("aria-hidden", "true");

  const icon = document.createElement("span");
  icon.className = "hover-idle-mpm-enabled-toggle-icon";
  icon.setAttribute("aria-hidden", "true");
  icon.innerHTML = options.iconSource ?? checkIconSource;

  button.append(fill, icon);

  const controller = options.pointerModel
    ? new MpSlotChargeController({
        element: button,
        pointerModel: options.pointerModel,
        getChargeMs: options.getChargeMs,
        onAssign: () => options.onEnabledChange(true),
        onRemove: () => options.onEnabledChange(false),
      })
    : null;

  return {
    element: button,
    clearPointerHover(): void {
      controller?.clearPointerHover();
    },
    sync(state): void {
      const disabled = state.disabled ?? false;
      button.disabled = disabled;
      button.dataset.enabled = state.enabled ? "true" : "false";
      button.setAttribute("aria-pressed", state.enabled ? "true" : "false");
      if (state.ariaLabel !== undefined) {
        button.setAttribute("aria-label", state.ariaLabel);
      }
      controller?.setInputLocked(disabled);
      controller?.sync({
        isAssigned: state.enabled,
        canAssign: !disabled,
        canRemove: !disabled,
      });
    },
    dispose(): void {
      controller?.dispose();
    },
  };
}
